---
title: "Локальные инструменты разработчика в браузере - где это реально безопасно"
description: "Когда JSON, YAML, Base64, UUID и regex можно проверять в браузере без отправки payload на чужой сервер, а когда лучше взять локальный CLI."
locale: "ru"
date: "2026-07-01"
tags: ["devtools", "privacy", "local"]
relatedTools: ["jsonFormatter", "yamlFormatter", "base64", "uuidGenerator", "regexTester"]
translations:
  en: "local-developer-tools-in-browser"
faq:
  - q: "devdeck отправляет JSON, YAML, Base64 или regex на сервер?"
    a: "Нет. Текущие инструменты devdeck работают в браузере. В Network tab должны быть видны ресурсы сайта и analytics metadata, а не ваш payload."
  - q: "Base64 можно считать защитой данных?"
    a: "Нет. Base64 - это кодирование, а не шифрование. Любой человек может декодировать строку обратно."
  - q: "JSON formatter проверяет бизнес-схему?"
    a: "Нет. Он проверяет синтаксис JSON через parser браузера. Типы полей, обязательные ключи и бизнес-правила нужно проверять отдельной schema validation."
  - q: "Regex tester использует PCRE?"
    a: "Нет. Он использует JavaScript RegExp текущего браузера. Поведение может отличаться от PCRE, RE2, Java или Python."
---

Секрет из staging попал в JSON payload. В нём bearer token, user id,
пара внутренних фича-флагов и кусок ответа API на 300 строк. Нужно
быстро отформатировать, найти сломанную запятую и не положить этот
текст в чужую историю запросов.

Для таких задач браузерный инструмент нормален только при одном условии:
обработка должна идти прямо в текущей вкладке. Не "мы удаляем через час",
не "мы не смотрим ваши файлы", а банально: открыть DevTools, Network,
нажать кнопку и увидеть, что payload никуда не улетел.

В devdeck текущий набор сделан именно так:
[JSON formatter](/json-formatter), [YAML formatter](/yaml-formatter),
[Base64](/base64), [UUID generator](/uuid-generator) и
[Regex tester](/regex-tester) работают локально в браузере.

## Быстрая проверка перед вставкой данных

| Что вставляете | Можно в devdeck | Оговорка |
| --- | --- | --- |
| API response в JSON | Да, если это текстовый JSON разумного размера | Проверяется синтаксис, не бизнес-схема |
| Kubernetes YAML | Да, для formatting и parser errors | Валидность манифеста против кластера не проверяется |
| Base64 token | Да, для encode/decode | Base64 не скрывает данные |
| UUID для тестовых записей | Да | Сгенерированные UUID годятся как dev/test id, не как гарантия порядка в чужой БД |
| Regex pattern и тестовый лог | Да | Диалект - JavaScript RegExp, не PCRE |

Если payload большой настолько, что вкладка замирает на секунды, это уже
не вопрос приватности. Это вопрос инструмента. Для 50 МБ JSON лучше
`jq`, для YAML из CI - локальный parser в репозитории, для regex на
боевых логах - тест рядом с кодом.

## Где заканчивается "локально"

Локальная обработка не делает данные магически безопасными. Она просто
убирает один класс риска: отправку текста на сервер инструмента. Остаются
буфер обмена, расширения браузера, скриншоты, корпоративный DLP, история
страницы, если вы сами её сохраняете.

С Base64 это особенно важно. Строка вида
`eyJ1c2VySWQiOjEyMywicm9sZSI6ImFkbWluIn0=` выглядит как токен, но это
обычная JSON-строка после кодирования. Декодировать её может кто угодно.
[Base64 инструмент](/base64) полезен для отладки, но не заменяет
шифрование и не превращает секрет в безопасный текст.

С JSON похожая ловушка. [JSON formatter](/json-formatter) говорит
"валидно", если документ можно разобрать как JSON. Он не знает, что
`price` должен быть числом, `status` должен быть одним из пяти значений,
а `createdAt` должен быть ISO date. Для этого нужна JSON Schema,
Zod, OpenAPI validator или проверка в вашем коде.

## Почему не просто поставить CLI

CLI часто лучше. `jq`, `yq`, `uuidgen`, `openssl base64`, тесты вокруг
regex - всё это нормально для постоянной работы в проекте.

Браузерный тул выигрывает в другом месте: когда вы на чужой машине,
проверяете один payload из issue, быстро сравниваете два варианта regex
или генерируете 20 UUID для тестового seed. Не надо ставить пакет,
искать флаг, вспоминать команду. Открыли вкладку, сделали, закрыли.

## Что проверять в Network tab

Откройте DevTools, вкладку Network, очистите список запросов и нажмите
кнопку инструмента. Для локального форматтера не должно появляться
POST/PUT/PATCH или XHR/fetch-запроса с вашим текстом. Запросы за JS,
CSS, favicon и analytics могут быть, но source payload не должен уходить
как body, query или часть URL.

Это простая проверка, зато она честнее страницы privacy policy.
Политика может обещать удаление через час. Network tab показывает, был
ли upload вообще.

Дальше порядок простой: для коротких dev payload - devdeck; для больших
файлов, production secrets и обязательной schema validation - локальный
CLI или тест в репозитории.
